import { Button, Col, Form, Input, type InputRef, Row, Select, Switch } from 'antd'
import { useCallback, type FunctionComponent, useEffect, useRef } from 'react'
import { useAppstreamsQuery } from '../CourseServers/query'
import { useCreateStudentMutation, useEditStudentMutation } from './mutation'

interface ParticipantFormProps {
  item: any
  courseId: number
  onSuccess: () => void
}

export const ParticipantForm: FunctionComponent<ParticipantFormProps> = ({
  courseId,
  item,
  onSuccess
}) => {
  const { data: appstreams } = useAppstreamsQuery(courseId)
  const updateMutation = useEditStudentMutation(courseId, item?.id)
  const createMutation = useCreateStudentMutation(courseId)
  const isCreate = item == null
  const mutation = isCreate ? createMutation : updateMutation
  const [form] = Form.useForm()
  const selectedAppstreamId = Form.useWatch('appstream_id', form)
  const selectedAppstream = appstreams?.find(
    (appstream) => appstream.id === selectedAppstreamId
  )

  useEffect(() => {
    if (selectedAppstream != null && form.isFieldTouched('appstream_id')) {
      form.resetFields(['reserve_server_id'])
    }
  }, [form, selectedAppstream])

  const formSubmitHandler = useCallback(
    (values) => {
      mutation.mutate(values, {
        onSuccess: () => {
          onSuccess?.()
        }
      })
    },
    [mutation, onSuccess]
  )

  const upperFirstLetter = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
  }

  const generateUsername = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isCreate) {
      return
    }
    const email = e.target.value
    if (email) {
      const username = email.substring(0, email.indexOf('@'))
      const p = username.split('.')
      let firstName = upperFirstLetter(username)
      let lastName = ''
      if (p.length >= 2) {
        firstName = upperFirstLetter(p[0])
        lastName = upperFirstLetter(p[1])
      } else {
        firstName = upperFirstLetter(username)
      }
      form.setFieldValue('first_name', firstName)
      form.setFieldValue('last_name', lastName)
      form.setFieldValue('username', username.toLowerCase())
      form.setFieldValue('alias', `${firstName} ${lastName}`.trim())
    }
  }

  const emailRef = useRef<InputRef>(null)
  useEffect(() => {
    setTimeout(() => emailRef.current?.focus(), 300)
  }, [])

  return (
    <Form
      layout='vertical'
      form={form}
      onFinish={formSubmitHandler}
      initialValues={{
        password: 'Welcome1!',
        is_passkey_required: false,
        ...item,
        avw_role: item?.avw_role ?? 'User'
      }}
    >
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item
            name='email'
            label='Email'
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'The input is not valid email' }
            ]}
          >
            <Input
              ref={emailRef}
              placeholder='Please enter email'
              onChange={generateUsername}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item
            name='first_name'
            label='First name'
            rules={[
              {
                required: true,
                message: 'Please enter first name'
              }
            ]}
          >
            <Input placeholder='Please enter first name' />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item
            name='last_name'
            label='Last name'
            rules={[{ required: true, message: 'Please enter last name' }]}
          >
            <Input placeholder='Please enter last name' />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item
            name='username'
            label='Username'
            rules={[
              {
                pattern: /^[\da-z.]+$/,

                message:
                  'Username should be only lowercase letters and dots (example: user.name)'
              }
            ]}
          >
            <Input placeholder='Please enter username' />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item name='password' label='Password'>
            <Input placeholder='Please enter password' />
          </Form.Item>
        </Col>
      </Row>
      {isCreate && (
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name='avw_role' label='AVW User Role'>
              <Select
                placeholder='Please select'
                options={[
                  { lable: 'User', value: 'User' },
                  { lable: 'Super Admin', value: 'Super Admin' }
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
      )}
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item label='Appstream' name='appstream_id'>
            <Select
              showSearch
              placeholder='Please select appstream'
              optionFilterProp='label'
              options={appstreams?.map((appstream) => ({
                label: appstream.stack_name,
                value: appstream.id
              }))}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item label='Fixed to server' name='reserve_server_id'>
            <Select
              showSearch
              placeholder='Fixed to a server or leave empty for dynamic allocation (optional)'
              optionFilterProp='label'
              options={selectedAppstream?.servers?.map((item) => ({
                label: item.name,
                value: item.id
              }))}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item name='alias' label='Alias'>
            <Input placeholder='Please enter alias' />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item name='is_passkey_required' label='Passkey Required' valuePropName='checked'>
            <Switch />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={24}>
          <Button
            type='primary'
            htmlType='submit'
            loading={mutation.isPending}
            disabled={mutation.isPending}
          >
            Submit
          </Button>
        </Col>
      </Row>
    </Form>
  )
}
